import React from "react";
import {useTranslation} from "react-i18next";
import styled from "styled-components";
import {WATERSPEED_PLANS_LINK} from "constants/AppConstants.js";
import {ws} from "styles/tokens";

/**
 * Free / Pro / Ultra with their amounts and billing periods, on the signed-out
 * landing page. Paddle's domain review needs to see prices and what each plan
 * includes without logging in. Informative only: no buy button, and the prices
 * are fixed here, so update PRICES when the store prices change.
 */

type TierKey = "free" | "premium" | "ultra";

/** USD list prices, matching the noscript block in public/index.html. */
const CURRENCY = "USD";
const PRICES: Record<TierKey, {monthly: number; yearly: number}> = {
  free: {monthly: 0, yearly: 0},
  premium: {monthly: 3.49, yearly: 29.99},
  ultra: {monthly: 9.99, yearly: 89}
};

const formatPrice = (amount: number, locale: string): string => {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: CURRENCY,
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2
    }).format(amount);
  } catch {
    return `$${amount}`;
  }
};

const TIERS: Array<{key: TierKey; name: string; features: string[]}> = [
  {key: "free", name: "free", features: ["freeFeature1", "freeFeature2", "freeFeature3", "freeFeature4"]},
  {key: "premium", name: "pro", features: ["proFeature1", "proFeature2", "proFeature3", "proFeature4", "proFeature5"]},
  {key: "ultra", name: "ultra", features: ["ultraFeature1", "ultraFeature2", "ultraFeature3", "ultraFeature4"]}
];

const Section = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  width: 100%;
  padding: 48px 120px 24px;

  @media (max-width: 980px) {
    padding: 40px 16px 16px;
  }
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  max-width: 720px;
  text-align: center;
`;

const Eyebrow = styled.span`
  font-family: ${ws.fontBody};
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${ws.accentText};
`;

const Title = styled.h2`
  margin: 0;
  font-family: ${ws.fontDisplay};
  font-size: 34px;
  font-weight: 300;
  line-height: 42px;
  letter-spacing: -0.02em;
  color: ${ws.fg};
`;

const Subtitle = styled.p`
  margin: 0;
  font-family: ${ws.fontBody};
  font-size: 16px;
  line-height: 24px;
  color: ${ws.muted};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
  width: 100%;
  max-width: 1080px;

  @media (max-width: 980px) {
    grid-template-columns: minmax(0, 1fr);
    max-width: 480px;
  }
`;

const Card = styled.article<{highlight: boolean}>`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  border: ${(props) => (props.highlight ? `2px solid ${ws.accent}` : `1px solid ${ws.line}`)};
  border-radius: ${ws.radiusXl};
  background: ${ws.card};
  box-shadow: ${ws.shadowSoft};
`;

const TierName = styled.h3`
  margin: 0;
  font-family: ${ws.fontBody};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${ws.accentText};
`;

const PriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 72px;
`;

const Amount = styled.p`
  margin: 0;
  font-family: ${ws.fontDisplay};
  font-size: 32px;
  font-weight: 500;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: ${ws.fg};

  span {
    font-family: ${ws.fontBody};
    font-size: 15px;
    font-weight: 400;
    letter-spacing: 0;
    color: ${ws.muted};
  }
`;

const PriceNote = styled.p`
  margin: 0;
  font-family: ${ws.fontBody};
  font-size: 14px;
  color: ${ws.muted};
  font-variant-numeric: tabular-nums;
`;

const Features = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    position: relative;
    padding-left: 22px;
    font-family: ${ws.fontBody};
    font-size: 14px;
    line-height: 20px;
    color: ${ws.fg};
  }

  li::before {
    content: "";
    position: absolute;
    top: 5px;
    left: 2px;
    width: 10px;
    height: 6px;
    border-left: 2px solid ${ws.accent};
    border-bottom: 2px solid ${ws.accent};
    transform: rotate(-45deg);
  }
`;

const Terms = styled.p`
  margin: 0;
  max-width: 720px;
  font-family: ${ws.fontBody};
  font-size: 13px;
  line-height: 20px;
  color: ${ws.muted};
  text-align: center;
`;

const CompareLink = styled.a`
  font-family: ${ws.fontBody};
  font-size: 15px;
  font-weight: 600;
  color: ${ws.accentText};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

function PricingSection() {
  const {t, i18n} = useTranslation();
  const locale = i18n.language || "en";

  const renderPrice = (tier: TierKey) => {
    const {monthly, yearly} = PRICES[tier];
    return (
      <PriceBlock>
        <Amount>
          {formatPrice(monthly, locale)}
          {tier !== "free" && <span>{t("subscribe.perMonth")}</span>}
        </Amount>
        <PriceNote>
          {tier === "free" ? t("pricing.freeNote") : t("pricing.orYearly", {price: formatPrice(yearly, locale)})}
        </PriceNote>
      </PriceBlock>
    );
  };

  return (
    <Section id="pricing" aria-labelledby="pricing-title">
      <Heading>
        <Eyebrow>{t("pricing.eyebrow")}</Eyebrow>
        <Title id="pricing-title">{t("pricing.title")}</Title>
        <Subtitle>{t("pricing.subtitle")}</Subtitle>
      </Heading>
      <Grid>
        {TIERS.map((tier) => (
          <Card key={tier.key} highlight={tier.key === "premium"}>
            <TierName>{t(`pricing.${tier.name}Name`)}</TierName>
            {renderPrice(tier.key)}
            <Features>
              {tier.features.map((feature) => (
                <li key={feature}>{t(`pricing.${feature}`)}</li>
              ))}
            </Features>
          </Card>
        ))}
      </Grid>
      <Terms>{t("pricing.terms")}</Terms>
      <CompareLink href={WATERSPEED_PLANS_LINK} target="_blank" rel="noreferrer">
        {t("pricing.compareAll")}
      </CompareLink>
    </Section>
  );
}

export default PricingSection;
